import { useState } from 'react';
import { useGallery } from '../hooks/useGallery';
import { useGallerySelection } from '../hooks/useGallerySelection';
import { useMediaDownload } from '../hooks/useMediaDownload';
import type { DownloadableItem } from '../hooks/useMediaDownload';
import { apiGet } from '../api/client';
import { GalleryToolbar } from './GalleryToolbar';
import { LoadMoreSentinel } from './LoadMoreSentinel';

const API_BASE = import.meta.env.VITE_API_BASE as string;
const ENDPOINT = '/api/tools/documents';

interface DocumentItem {
  id: string;
  title: string;
  filename_slug: string;
  skill_name: string | null;
  size_bytes: number;
  koji_codename: string;
  status: string;
  created_at: number;
  signedUrl: string | null;
  preview: string | null;
}

interface DocumentGetResponse {
  document: DocumentItem & { r2_key: string };
  body: string;
}

function fmtBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(2)} MB`;
}

function fmtAgo(unixSec: number): string {
  const ms = Date.now() - unixSec * 1000;
  if (ms < 60_000) return 'just now';
  if (ms < 3_600_000) return `${Math.round(ms / 60_000)} min ago`;
  if (ms < 86_400_000) return `${Math.round(ms / 3_600_000)} h ago`;
  return new Date(unixSec * 1000).toLocaleString();
}

/**
 * Stage 28: Documents gallery — artifacts written by the write_markdown_file
 * tool. Uses the shared useGallery + useGallerySelection + useMediaDownload
 * primitives so selection-driven single and ZIP downloads come for free.
 * Clicking a row opens a modal with the full body rendered in a code block.
 */
export function DocumentGallery(): JSX.Element {
  const { items, loading, error, deleteItem, loadMore, hasMore } =
    useGallery<DocumentItem>(ENDPOINT);
  const selection = useGallerySelection(ENDPOINT);
  const download = useMediaDownload({
    zipEndpoint: '/api/tools/documents/zip',
    kind: 'document',
  });
  const [active, setActive] = useState<DocumentGetResponse | null>(null);
  const [viewLoading, setViewLoading] = useState(false);

  const allIds = items.map((d) => d.id);

  const selectedDownloadItems: DownloadableItem[] = items
    .filter((d) => selection.selected.has(d.id))
    .map((d) => ({
      id: d.id,
      signedUrl: d.signedUrl,
      status: d.status,
      title: d.title,
      koji_codename: d.koji_codename,
      mime_type: 'text/markdown',
    }));

  const openView = async (id: string): Promise<void> => {
    setViewLoading(true);
    try {
      const res = await apiGet<DocumentGetResponse>(
        `${ENDPOINT}/${encodeURIComponent(id)}`,
      );
      setActive(res);
    } catch (err) {
      console.warn('[documents] fetch body failed:', err);
    } finally {
      setViewLoading(false);
    }
  };

  return (
    <section className="gallery gallery-documents">
      <GalleryToolbar
        selection={selection}
        allIds={allIds}
        download={{
          selectedItems: selectedDownloadItems,
          downloadOne: download.downloadOne,
          downloadZip: download.downloadZip,
          busy: download.busy,
          error: download.error,
        }}
      />
      {loading && items.length === 0 ? (
        <div className="muted">Loading documents…</div>
      ) : error ? (
        <div className="status-missing">Error: {error}</div>
      ) : items.length === 0 ? (
        <p className="muted gallery-empty">
          No documents yet. Ask the assistant to produce a plan, spec, or
          report — skills like TINS will save their output here.
        </p>
      ) : (
        <ul className="track-list">
          {items.map((d) => {
            const isSelected = selection.selected.has(d.id);
            return (
              <li
                key={d.id}
                className={
                  'track-row' + (isSelected ? ' track-row-selected' : '')
                }
              >
                {selection.selectMode ? (
                  <label className="gallery-select-label">
                    <input
                      type="checkbox"
                      checked={isSelected}
                      onChange={() => selection.toggleSelected(d.id)}
                      aria-label={`Select ${d.title}`}
                    />
                  </label>
                ) : (
                  <span
                    style={{ fontSize: '1.3em', paddingLeft: 4 }}
                    aria-hidden="true"
                  >
                    📄
                  </span>
                )}
                <div
                  className="track-meta"
                  onClick={() => {
                    if (!selection.selectMode && !viewLoading) {
                      void openView(d.id);
                    }
                  }}
                  style={{
                    cursor: selection.selectMode ? 'default' : 'pointer',
                  }}
                >
                  <div className="track-title">{d.title}</div>
                  <div className="muted small">
                    {d.skill_name ? (
                      <span className="badge" style={{ marginRight: 6 }}>
                        {d.skill_name}
                      </span>
                    ) : null}
                    {fmtBytes(d.size_bytes)} · {fmtAgo(d.created_at)} ·{' '}
                    {d.koji_codename}
                  </div>
                  {d.preview ? (
                    <div className="muted small" style={{ marginTop: 4 }}>
                      {d.preview}
                      {d.preview.length >= 140 ? '…' : ''}
                    </div>
                  ) : null}
                </div>
                {!selection.selectMode ? (
                  <button
                    className="btn-danger small"
                    onClick={() => {
                      if (confirm('Delete this document?')) {
                        void deleteItem(d.id);
                      }
                    }}
                  >
                    Delete
                  </button>
                ) : null}
              </li>
            );
          })}
        </ul>
      )}

      {active ? (
        <div
          className="modal-backdrop"
          role="dialog"
          aria-modal="true"
          onClick={(e) => {
            if (e.target === e.currentTarget) setActive(null);
          }}
        >
          <div className="modal" style={{ maxWidth: 900 }}>
            <header className="modal-header">
              <h2>{active.document.title}</h2>
              <button
                className="modal-close"
                onClick={() => setActive(null)}
                aria-label="Close"
              >
                ×
              </button>
            </header>
            <section className="modal-body">
              <pre
                className="code-block"
                style={{
                  whiteSpace: 'pre-wrap',
                  maxHeight: '70vh',
                  overflowY: 'auto',
                }}
              >
                <code>{active.body}</code>
              </pre>
            </section>
            <footer
              className="modal-footer"
              style={{ display: 'flex', gap: 8 }}
            >
              {active.document.signedUrl ? (
                <a
                  className="btn-primary"
                  href={
                    active.document.signedUrl.startsWith('http')
                      ? active.document.signedUrl
                      : `${API_BASE}${active.document.signedUrl}`
                  }
                  download={`${active.document.filename_slug}.md`}
                >
                  Download .md
                </a>
              ) : null}
              <button onClick={() => setActive(null)}>Close</button>
            </footer>
          </div>
        </div>
      ) : null}

      <LoadMoreSentinel
        hasMore={hasMore}
        loading={loading}
        onReach={() => void loadMore()}
      />
    </section>
  );
}
